import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';

import { ProtectedRoute } from '@/shared/components/layout/ProtectedRoute';
import { MainLayout } from '@/shared/components/layout/MainLayout';

// Auth pages
import { Login, Register, ResetPassword } from '@/modules/auth/pages';

// Dashboard page
import { DashboardPage } from '@/modules/dashboard/pages';

// Dashboards
import { HomeRedirect } from '@/shared/components/layout/HomeRedirect';
import { CommercialDashboardPage } from '@/modules/dashboards-commercial/pages';
import { VisitsAnalyticsPage } from '@/modules/dashboards-analytics/pages';
import { MarketingDashboardPage } from '@/modules/dashboards-marketing/pages';

// CRM main page
import { CrmPage } from '@/modules/crm/pages';

// CRM Leads page
import { LeadsListPage } from '@/modules/crm/leads/pages';

// Dashboards > Usuário — Minhas Vendas (antiga "Dashboard do vendedor", spec §4.6)
import { SellerDashboardPage } from '@/modules/crm/seller-dashboard/pages';

// CRM Customers pages
import { CustomersPage, CustomerFormPage, CustomerDetailPage } from '@/modules/crm/customers/pages';

// Calendar page
import { CalendarPage } from '@/modules/calendar/pages/CalendarPage';

// Chat pages
import { ChatPage } from '@/modules/chat/pages';
import { HistoryPage } from '@/modules/chat/pages/HistoryPage';

// Attendance Management pages
import { AttendanceManagementPageImproved as AttendanceManagementPage } from '@/modules/attendance-management/pages/AttendanceManagementPageImproved';

// Billing pages
import { BillingPage, PlansPage } from '@/modules/billing/pages';

// Channels pages
import { ChannelsPage, EditChannelPage, ChannelDashboardPage } from '@/modules/channels/pages';

// Account pages
import { AccountPage } from '@/modules/account/pages';

// Users pages
import { UsersPage, UserFormPage } from '@/modules/users/pages';

// Levels pages
import { LevelsPage, LevelFormPage } from '@/modules/levels/pages';

// Goals pages
import { GoalsPage } from '@/modules/goals/pages';

// Ranking de gamificação (Parte 4)
import { RankingPage } from '@/modules/crm/ranking/pages/RankingPage';

// Gamificação — Configurações (Parte 4)
import { GamificationSettingsPage } from '@/modules/gamification/pages/GamificationSettingsPage';

// Notifications log page
import { NotificationsLogPage } from '@/modules/notifications/pages/NotificationsLogPage';

// Teams pages
import { TeamsPage, TeamFormPage } from '@/modules/teams/pages';

// Units pages
import { TagsPage } from '@/modules/tags/pages';
import { UnitsPage } from '@/modules/units/pages';

// Shifts pages
import { ShiftsPage, ShiftFormPage } from '@/modules/shifts/pages';

// SLA policies page
import { SlaPoliciesPage } from '@/modules/sla-policies/pages';

// API Keys pages
import { ApiKeysPage } from '@/modules/api-keys/pages';

// Templates pages
import { TemplatesPage, TemplateFormPage } from '@/modules/templates/pages';

// Audiences pages
import { AudiencesPage, AudienceDetailPage } from '@/modules/audiences/pages';

// Automation pages
import { AutomationPage, WorkflowsPage, AIAgentsPage } from '@/modules/automation';
import { AgentFormPage } from '@/modules/automation/ai-agents/pages';
import { CustomActionsPage, CustomActionFormPage } from '@/modules/automation/custom-actions/pages';
import { WorkflowEditorPage } from '@/modules/workflows/pages';
import { SkillsPage } from '@/modules/skills/pages/SkillsPage';
import { SkillEditorPage } from '@/modules/skills/pages/SkillEditorPage';
import { VoicesPage, VoiceFormPage } from '@/modules/automation/voices/pages';
import { FormsPage, FormBuilderPage, FormReportPage } from '@/modules/automation/forms/pages';
import { FormPublicPage } from '@/modules/automation/forms/public';

// Captura de leads por QR (página pública, cliente final — Parte 2, Task 16)
import { LeadCapturePublicPage } from '@/modules/crm/capture/public/LeadCapturePublicPage';

// Checklists pages (Task 15)
import { ChecklistsPage } from '@/modules/checklists/pages/ChecklistsPage';
import { ChecklistTemplatesPage } from '@/modules/checklists/pages/ChecklistTemplatesPage';
import { ChecklistReviewPage } from '@/modules/checklists/pages/ChecklistReviewPage';
// Checklists — operador em campo (Task 16)
import { MyChecklistsPage } from '@/modules/checklists/pages/MyChecklistsPage';
import { ChecklistFillPage } from '@/modules/checklists/pages/ChecklistFillPage';

// Campaigns pages
import { CampaignsPage, CampaignFormPage, CampaignDetailPage } from '@/modules/campaigns/pages';

// Databases pages
import { DatabasesPage, DocumentDetailsPage, DocumentFormPage } from '@/modules/databases/pages';

// Websites pages
import { WebsitesListPage, WebsiteCustomizerPage } from '@/modules/websites/pages';

// CRM Reports pages
import { CRMReportsPage } from '@/modules/crm-reports/pages/CRMReportsPage';
import { CsatReportPage } from '@/modules/csat-report/pages/CsatReportPage';


// Help Desk pages
import { TicketsPage, TicketDetailPage } from '@/modules/helpdesk/tickets/pages';
import { ProjectsPage, ProjectFormPage, ProjectWorkspacePage } from '@/modules/helpdesk/projects/pages';
import { RetentionPage } from '@/modules/helpdesk/retention/pages';

// Email module
import { EmailPage } from '@/modules/email/pages/EmailPage';

// Email Domains pages
import { EmailDomainsPage, EmailDomainDetailPage } from '@/modules/email-domains/pages';

// Settings pages (CustomizationPage moved into CompanyEditDrawer)

// Other pages
import { Unauthorized, NotFound } from '@/pages';

const routes: RouteObject[] = [
  // Public routes
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/register',
    element: <Register />,
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
  },
  {
    path: '/f/:slug',
    element: <FormPublicPage />,
  },
  {
    // Captura por QR (cliente final, sem login). Caddy `try_files` já cobre a rota.
    path: '/leads/capture/:uuid',
    element: <LeadCapturePublicPage />,
  },

  // Protected routes
  {
    path: '/',
    element: <ProtectedRoute requireAuth />,
    children: [
      {
        path: '',
        element: <MainLayout />,
        children: [
          {
            index: true,
            element: <HomeRedirect />,
          },
          {
            path: 'dashboard/management/support',
            element: <DashboardPage />,
          },
          {
            path: 'dashboard/management/commercial',
            element: <CommercialDashboardPage />,
          },
          {
            path: 'dashboard/management/marketing',
            element: <MarketingDashboardPage />,
          },
          {
            path: 'dashboard/management/analytics',
            element: <VisitsAnalyticsPage />,
          },
          {
            path: 'dashboard/management/sales',
            element: <SellerDashboardPage />,
          },
          {
            path: 'crm',
            element: <CrmPage />,
          },
          {
            // Endereço antigo da tela do vendedor, que virou
            // "Dashboards > Usuário > Minhas Vendas" em 23/08/2026. Fica como
            // redirecionamento porque é link salvo por gente que usa a tela
            // todo dia — e porque `landingPage: 'crm-seller'`, gravado nos
            // níveis de acesso, apontava para cá.
            path: 'crm/seller',
            element: <Navigate to="/dashboard/management/sales" replace />,
          },
          {
            path: 'crm/leads',
            element: <LeadsListPage />,
          },
          // Rotas de customers ESPECÍFICAS antes de `crm/:funnelId` (genérica)
          {
            path: 'crm/customers',
            element: <CustomersPage />,
          },
          {
            path: 'crm/customers/new',
            element: <CustomerFormPage />,
          },
          {
            path: 'crm/customers/:id',
            element: <CustomerDetailPage />,
          },
          {
            path: 'crm/customers/:id/edit',
            element: <CustomerFormPage />,
          },
          // Ranking de gamificação (Parte 4) — específica antes de `crm/:funnelId`.
          {
            path: 'crm/ranking',
            element: <RankingPage />,
          },
          {
            path: 'crm/:funnelId',
            element: <CrmPage />,
          },
          {
            path: 'calendar',
            element: <CalendarPage />,
          },
          {
            path: 'chat',
            element: <ChatPage />,
          },
          {
            path: 'chat/:conversationId',
            element: <ChatPage />,
          },
          {
            path: 'chat/management',
            element: <AttendanceManagementPage />,
          },
          {
            path: 'history',
            element: <HistoryPage />,
          },
          {
            path: 'history/:contactId/:channelId',
            element: <HistoryPage />,
          },
          {
            path: 'billing',
            element: <BillingPage />,
          },
          {
            path: 'billing/plans',
            element: <PlansPage />,
          },
          {
            path: 'notifications',
            element: <NotificationsLogPage />,
          },
          {
            path: 'settings/channels',
            element: <ChannelsPage />,
          },
          {
            path: 'settings/channels/:id/edit',
            element: <EditChannelPage />,
          },
          {
            path: 'settings/channels/:id/dashboard',
            element: <ChannelDashboardPage />,
          },
          {
            path: 'settings',
            element: <AccountPage />,
          },
          {
            path: 'settings/account',
            element: <AccountPage />,
          },
          {
            path: 'settings/notifications',
            element: <AccountPage />,
          },
          {
            path: 'settings/security',
            element: <AccountPage />,
          },
          {
            path: 'settings/users',
            element: <UsersPage />,
          },
          {
            path: 'settings/users/create',
            element: <UserFormPage />,
          },
          {
            path: 'settings/users/:id/edit',
            element: <UserFormPage />,
          },
          {
            path: 'settings/levels',
            element: <LevelsPage />,
          },
          {
            path: 'settings/levels/new',
            element: <LevelFormPage />,
          },
          {
            path: 'settings/levels/:id/edit',
            element: <LevelFormPage />,
          },
          {
            path: 'settings/goals',
            element: <GoalsPage />,
          },
          {
            path: 'settings/gamification',
            element: <GamificationSettingsPage />,
          },
          {
            path: 'settings/teams',
            element: <TeamsPage />,
          },
          {
            path: 'settings/teams/new',
            element: <TeamFormPage />,
          },
          {
            path: 'settings/teams/:id/edit',
            element: <TeamFormPage />,
          },
          {
            path: 'settings/shifts',
            element: <ShiftsPage />,
          },
          {
            path: 'settings/shifts/new',
            element: <ShiftFormPage />,
          },
          {
            path: 'settings/shifts/:id/edit',
            element: <ShiftFormPage />,
          },
          // Políticas de SLA (prazos de primeira resposta e resolução).
          // Configuração, não relógio: o card do chamado/conversa é quem exibe
          // o tempo, e ele não passa por aqui.
          {
            path: 'settings/sla-policies',
            element: <SlaPoliciesPage />,
          },
          {
            path: 'integration/api-keys',
            element: <ApiKeysPage />,
          },
          {
            path: 'prospecting/templates',
            element: <TemplatesPage />,
          },
          {
            path: 'prospecting/templates/new',
            element: <TemplateFormPage />,
          },
          {
            path: 'prospecting/templates/:id/edit',
            element: <TemplateFormPage />,
          },
          {
            path: 'prospecting/audiences',
            element: <AudiencesPage />,
          },
          {
            path: 'prospecting/audiences/:id',
            element: <AudienceDetailPage />,
          },
          {
            path: 'automation',
            element: <AutomationPage />,
          },
          {
            path: 'automation/workflows',
            element: <WorkflowsPage />,
          },
          {
            path: 'automation/workflows/folder/:folderId',
            element: <WorkflowsPage />,
          },
          {
            path: 'automation/workflows/new',
            element: <WorkflowEditorPage />,
          },
          {
            path: 'automation/workflows/:id',
            element: <WorkflowEditorPage />,
          },
          {
            path: 'automation/skills',
            element: <SkillsPage />,
          },
          {
            path: 'automation/skills/new',
            element: <SkillEditorPage />,
          },
          {
            path: 'automation/skills/:id',
            element: <SkillEditorPage />,
          },
          {
            path: 'automation/ai-agents',
            element: <AIAgentsPage />,
            children: [
              {
                path: 'new',
                element: <AgentFormPage />,
              },
              {
                path: ':id/edit',
                element: <AgentFormPage />,
              }
            ]
          },
          {
            path: 'automation/custom-actions',
            element: <CustomActionsPage />,
          },
          {
            path: 'automation/custom-actions/new',
            element: <CustomActionFormPage />,
          },
          {
            path: 'automation/custom-actions/:id/edit',
            element: <CustomActionFormPage />,
          },
          // Voices
          {
            path: 'automation/voices',
            element: <VoicesPage />,
          },
          {
            path: 'automation/voices/new',
            element: <VoiceFormPage />,
          },
          {
            path: 'automation/voices/:id/edit',
            element: <VoiceFormPage />,
          },
          // Forms
          {
            path: 'automation/forms',
            element: <FormsPage />,
          },
          {
            path: 'automation/forms/new',
            element: <FormBuilderPage />,
          },
          {
            path: 'automation/forms/:id/edit',
            element: <FormBuilderPage />,
          },
          {
            path: 'automation/forms/:id/report',
            element: <FormReportPage />,
          },
          // Checklists — listagem de gestão, modelos e revisão (Task 15) +
          // operador em campo (Task 16).
          // Ordem: `checklists` → `checklists/my` → `checklists/templates`
          // (+ `/new`, `/:id/edit`, builder da Task 14) → `checklists/:id/fill`
          // → `checklists/:id` (genérica, SEMPRE #6) por último, senão `:id`
          // capturaria "templates"/"my".
          {
            path: 'checklists',
            element: <ChecklistsPage />,
          },
          // "Meus checklists" — caixa de entrada do operador (assignee/follower).
          {
            path: 'checklists/my',
            element: <MyChecklistsPage />,
          },
          {
            path: 'checklists/templates',
            element: <ChecklistTemplatesPage />,
          },
          // `type="checklist"` trava o modo do FormBuilderPage (paleta ganha
          // campos photo/conformity + seção "Configurações do checklist").
          {
            path: 'checklists/templates/new',
            element: <FormBuilderPage type="checklist" />,
          },
          {
            // `type="checklist"` também na EDIÇÃO: é o que faz o builder ler e
            // gravar em `/api/checklists/templates` (permissão `checklists:*`)
            // em vez de `/api/forms`. Sem a prop, o builder só descobriria o
            // tipo DEPOIS de carregar o documento — tarde demais, e por uma
            // rota que exige permissão de Formulários.
            path: 'checklists/templates/:id/edit',
            element: <FormBuilderPage type="checklist" />,
          },
          // Preenchimento em campo — ANTES da genérica `:id` (o segmento extra
          // já a distinguiria, mas a ordem explícita mantém a regra SEMPRE #6).
          {
            path: 'checklists/:id/fill',
            element: <ChecklistFillPage />,
          },
          {
            path: 'checklists/:id',
            element: <ChecklistReviewPage />,
          },
          {
            path: 'prospecting/campaigns',
            element: <CampaignsPage />,
          },
          {
            path: 'prospecting/campaigns/new',
            element: <CampaignFormPage />,
          },
          {
            path: 'prospecting/campaigns/:id',
            element: <CampaignDetailPage />,
          },
          {
            path: 'prospecting/campaigns/:id/edit',
            element: <CampaignFormPage />,
          },
          {
            path: 'databases',
            element: <DatabasesPage />,
          },
          {
            path: 'databases/:id',
            element: <DatabasesPage />,
          },
          {
            path: 'databases/:id/documents/new',
            element: <DocumentFormPage />,
          },
          {
            path: 'databases/:id/documents/:documentId',
            element: <DocumentDetailsPage />,
          },
          {
            path: 'databases/:id/documents/:documentId/edit',
            element: <DocumentFormPage />,
          },
          // Websites
          {
            path: 'websites',
            element: <WebsitesListPage />,
          },
          // Units (Unidades) — CRUD + membros (gerente/membros). Checklists
          // (Tasks 15-16) consomem `useUnitsDropdown` pra filtrar por unidade.
          // Migrou de `/units` para `/settings/units` em 2026-08-19, quando o
          // item saiu da barra principal e virou submenu de Configurações.
          {
            path: 'settings/units',
            element: <UnitsPage />,
          },
          // Endereço antigo — mantido só para não quebrar link salvo/favorito.
          {
            path: 'units',
            element: <Navigate to="/settings/units" replace />,
          },
          // Tags — catálogo de tags da empresa (22/09/2026). Vive em
          // Configurações, junto de Unidades e Equipes, porque é cadastro de
          // organização: define o vocabulário que o chat, o CRM, os tickets e
          // as automações vão usar. Aplicar uma tag continua sendo trabalho de
          // quem atende, sob a permissão da própria entidade.
          {
            path: 'settings/tags',
            element: <TagsPage />,
          },
          {
            path: 'websites/:id',
            element: <WebsiteCustomizerPage />,
          },
          // Help Desk
          {
            path: 'helpdesk/tickets',
            element: <TicketsPage />,
          },
          // Retenção (específica ANTES de `:id` — CLAUDE.md NUNCA #3)
          {
            path: 'helpdesk/tickets/retention',
            element: <RetentionPage />,
          },
          {
            path: 'helpdesk/tickets/:id',
            element: <TicketDetailPage />,
          },
          // Help Desk — Projetos (específica `/new` ANTES de `/:id`)
          {
            path: 'helpdesk/projects',
            element: <ProjectsPage />,
          },
          {
            path: 'helpdesk/projects/new',
            element: <ProjectFormPage />,
          },
          {
            path: 'helpdesk/projects/:id',
            element: <ProjectWorkspacePage />,
          },
          // Email
          {
            path: 'email',
            element: <EmailPage />,
          },
          {
            path: 'email/:conversationId',
            element: <EmailPage />,
          },
          // Customization (moved into CompanyEditDrawer — redirect for backwards compat)
          {
            path: 'settings/customization',
            element: <Navigate to="/settings" replace />,
          },
          // MCP Connections — aba dentro de Configurações da Conta (AccountPage)
          {
            path: 'settings/mcp',
            element: <AccountPage />,
          },
          // Email Domains
          {
            path: 'settings/email-domains',
            element: <EmailDomainsPage />,
          },
          {
            path: 'settings/email-domains/:id',
            element: <EmailDomainDetailPage />,
          },
          // CRM Reports
          {
            path: 'reports/crm',
            element: <CRMReportsPage />,
          },
          // Relatórios → CSAT (permissão própria `reports-csat`, 2026-09-23)
          {
            path: 'reports/csat',
            element: <CsatReportPage />,
          },
          // TODO: Add other protected routes here
          // {
          //   path: 'crm',
          //   element: <CrmModule />,
          //   children: [
          //     { path: 'leads', element: <LeadList /> },
          //     { path: 'pipelines', element: <PipelineList /> },
          //     { path: 'activities', element: <ActivityList /> },
          //   ],
          // },
          // ... other modules
        ],
      },
    ],
  },

  // Error routes
  {
    path: '/unauthorized',
    element: <Unauthorized />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export const router = createBrowserRouter(routes);